import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Trash2 } from "lucide-react";
import { API, client } from "@/lib/api-base";

/**
 * Studio branding tab — upload + preview + reset of the custom sidebar logo.
 * The logo file is stored server-side; the sidebar fetches the current version
 * and renders either the uploaded image or the default "PGI" monogram.
 */
export function StudioBrandingTab() {
  const [identity, setIdentity] = useState({ has_logo: false, logo_version: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get("/settings/identity");
      setIdentity(res.data || { has_logo: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Broadcast so the sidebar (mounted elsewhere) reloads its preview.
  const broadcastUpdate = () => {
    window.dispatchEvent(new CustomEvent("studio-logo-changed"));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File troppo grande (max 2 MB).");
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await client.post("/settings/identity/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo aggiornato.");
      await load();
      broadcastUpdate();
    } catch (err) {
      toast.error(formatApiError(err, "Errore caricamento logo"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Rimuovere il logo personalizzato e tornare al monogramma di default?")) return;
    setBusy(true);
    try {
      await client.delete("/settings/identity/logo");
      toast.success("Logo rimosso. Verrà mostrato il monogramma di default.");
      await load();
      broadcastUpdate();
    } catch (err) {
      toast.error(formatApiError(err, "Errore"));
    } finally {
      setBusy(false);
    }
  };

  const logoUrl = identity.has_logo
    ? `${API}/settings/identity/logo?v=${identity.logo_version}`
    : null;

  return (
    <Card data-testid="studio-branding-card">
      <CardHeader>
        <CardTitle>Identità studio</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-xs text-stone-600">
          Carica un'immagine personalizzata da usare come logo nella barra laterale.
          Formati supportati: PNG, JPG, WEBP, GIF. Dimensione massima 2 MB.
          Consigliato un quadrato 256×256 px con sfondo trasparente.
        </p>

        <div className="flex items-center gap-5">
          <div
            data-testid="studio-branding-preview"
            className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary"
          >
            {loading ? (
              <span className="text-[11px] text-stone-500">…</span>
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo studio"
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="font-serif text-2xl font-semibold text-primary">PGI</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-stone-800">
              {identity.has_logo ? "Logo personalizzato attivo" : "Monogramma di default"}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleUpload}
                className="hidden"
                data-testid="studio-branding-file-input"
              />
              <Button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="studio-branding-upload"
              >
                <Upload size={14} /> {identity.has_logo ? "Sostituisci logo" : "Carica logo"}
              </Button>
              {identity.has_logo && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={busy}
                  data-testid="studio-branding-reset"
                >
                  <Trash2 size={14} /> Rimuovi
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
